import React, { useMemo } from "react";
import { CHART_COLORS, EChartsRenderer } from 'components/charts';
import { NonIdealState } from "@blueprintjs/core/lib/esm/components/non-ideal-state/nonIdealState";
import { ShowHide } from "components/layout/ShowHide";
import classNames from "classnames";
import PropTypes from "prop-types";
import './chart.css';

export function MultiStackedBarChart({
    data,
    title,
    loading = false,
    xAxisName = 'X-Axis',
    yAxisName = 'YAxis',
    xAxisDataKey = 'x',
    seriesDataKey = 'series',
    chartHeight = '400px',
    chartWidth = '100%',
    barMinHeight = 0,
    splitNumber = 5,
    tooltipFormatter = null,
    dataTransformer = null,
    noDataMessage = 'No data to display.',
    controls = null
}) {

    const PADDING_25 = 25;
    const PADDING_20 = 20;

    const option = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return {};
        }

        const transformedData = dataTransformer
            ? dataTransformer(data)
            : [...data];

        const maxValue = Math.max(
            ...transformedData.map((month) =>
                month.series.reduce((total, item) => total + item.value, 0) // Sum all values in the series for the month
            )
        );
        
        const interval = Math.ceil(maxValue / splitNumber);
        const xAxisLabels = transformedData.map((item) => item[xAxisDataKey]);
        const labelSet = new Set();

        transformedData.forEach((item) => {
            (item[seriesDataKey] || []).forEach((series) => {
                labelSet.add(series.label);
            });
        });

        const seriesLabels = Array.from(labelSet);

        const seriesData = seriesLabels.map((label) => {
            const rawValues = transformedData.map((item) => {
                const seriesItem = (item[seriesDataKey] || []).find((s) => s.label === label);
                return seriesItem ? seriesItem.value : 0;
            });

            return {
                name: label,
                type: "bar",
                stack: "total",
                barWidth: "70%",
                barMinHeight: barMinHeight,
                emphasis: { focus: "series" },
                data: rawValues,
            };
        });

        return {
            color: CHART_COLORS,
            legend: { top: 0, type: "scroll", left: "right" },
            grid: { left: 60, right: 20, top: 60, bottom: 50 },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: tooltipFormatter || ((params) => {
                    const idx = params?.[0]?.dataIndex ?? 0;
                    const lines = [
                        `<b>${xAxisLabels[idx]}</b>`,
                        ...params.map((p) => `${p.marker}${p.seriesName}: ${p.value}`),
                    ];
                    return lines.join("<br/>");
                }),
            },
            xAxis: {
                type: 'category',
                data: xAxisLabels,
                name: xAxisName,
                axisLabel: { show: true },
                nameLocation: 'middle',
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold',
                    padding: [PADDING_20, 0, 0, 0]
                },
            },
            yAxis: {
                type: 'value',
                max: interval * splitNumber,
                splitNumber: splitNumber,
                interval: interval,
                name: yAxisName,
                nameLocation: 'middle',
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold',
                    padding: [0, 0, PADDING_25, 0]
                },
                axisLabel: { show: true },

            },
            series: seriesData
        };

    }, [data]);

    return (
        <div className="chart-container">
            <div className="chart-header">
                <div>
                    <span className='chart-title'>CHART</span>
                    <h3 className='spacer-bottom'>{title}</h3>
                </div>
                {controls && (
                    <div className="chart-controls">{controls}</div>
                )}
            </div>
            <ShowHide
                evaluator={loading || data?.length}
                show={(
                    <div className={classNames({ 'bp6-skeleton': loading })}>
                        <EChartsRenderer
                            option={option}
                            showLoading={loading}
                            style={{ height: chartHeight, width: chartWidth }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    </div>
                )}
                hide={<NonIdealState
                    title={''}
                    className='spacer-bottom-small'
                    description={noDataMessage}
                />}
            />
        </div>
    );
}

MultiStackedBarChart.propTypes = {
    data: PropTypes.array.isRequired,
    title: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    xAxisName: PropTypes.string,
    yAxisName: PropTypes.string,
    xAxisDataKey: PropTypes.string,
    seriesDataKey: PropTypes.string,
    chartHeight: PropTypes.string,
    chartWidth: PropTypes.string,
    barMinHeight: PropTypes.number,
    splitNumber: PropTypes.number,
    tooltipFormatter: PropTypes.func,
    dataTransformer: PropTypes.func,
    noDataMessage: PropTypes.string,
    controls: PropTypes.node,
};